<script setup lang="ts">
import type { TenantContext } from '~/types/types'
import type { SiteMode } from '~/constants/siteConfig'

/**
 * Themed wrapper for the tenant public site. Carries the data attributes
 * that activate the theme tokens in assets/css/site-themes.css and hosts
 * the site header/footer chrome.
 *
 * mode 'auto' (default) follows the platform light/dark toggle; the editor
 * preview passes an explicit mode instead.
 */
const props = withDefaults(defineProps<{
  tenant: TenantContext
  modeOverride?: SiteMode | null
}>(), { modeOverride: null })

useSiteMode(() => props.tenant.site)

// Tenant brand accent overrides the theme's accent only when explicitly set.
const accentStyle = computed(() => {
  const accent = props.tenant.site.accent_color
  return accent ? { '--site-accent': accent } : undefined
})
</script>

<template>
  <UApp>
    <div
      class="site-scope flex min-h-screen flex-col"
      :data-site-theme="tenant.site.theme"
      :data-site-mode="modeOverride ?? 'auto'"
      :style="accentStyle"
    >
      <SiteHeader :tenant="tenant" />
      <main class="flex-1">
        <slot />
      </main>
      <SiteFooter :tenant="tenant" />
    </div>
  </UApp>
</template>
